import React, { useEffect, useState } from "react";
import { Star, CheckCircle2, XCircle, AlertCircle, ExternalLink, RefreshCw, Save, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

function StatusBadge({ status }) {
    if (status === "ok")
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <CheckCircle2 className="w-3 h-3" /> Pripojené
            </span>
        );
    if (status === "error")
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                <XCircle className="w-3 h-3" /> Chyba
            </span>
        );
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-500/10 text-zinc-500 border border-zinc-700">
            <AlertCircle className="w-3 h-3" /> Nenastavené
        </span>
    );
}

function StarRow({ rating }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(n => (
                <Star key={n}
                    className={`w-3.5 h-3.5 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
}

function ReviewPreview({ review }) {
    return (
        <div className="flex gap-3 p-3 rounded-xl border border-white/6">
            {review.photo ? (
                <img src={review.photo} alt={review.author} className="w-8 h-8 rounded-full object-cover shrink-0 ring-1 ring-white/10" />
            ) : (
                <div className="w-8 h-8 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0 text-amber-400 font-bold text-sm">
                    {review.author?.charAt(0)}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-xs font-medium truncate">{review.author}</span>
                    <StarRow rating={review.rating} />
                </div>
                <p className="text-zinc-400 text-xs leading-relaxed mt-0.5 line-clamp-2">
                    {review.text || <span className="italic text-zinc-600">Bez textu</span>}
                </p>
                <span className="font-mono text-[10px] text-zinc-600">{review.time_desc}</span>
            </div>
        </div>
    );
}

export default function AdminGoogle() {
    const [settings, setSettings] = useState({ place_id: "", api_key_masked: "", place_name: "", maps_url: "" });
    const [apiKey, setApiKey] = useState("");
    const [showKey, setShowKey] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [reviews, setReviews] = useState(null);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        api.getGoogleSettings().then(s => {
            setSettings(s || {});
            setApiKey("");
        }).catch(() => {});
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.updateGoogleSettings({
                place_id: settings.place_id,
                api_key:  apiKey,
            });
            toast.success("Nastavenia uložené");
            setApiKey("");
            const updated = await api.getGoogleSettings();
            setSettings(updated || {});
        } catch (e) {
            toast.error(e.message || "Chyba pri ukladaní");
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.testGoogleConnection();
            setTestResult({ ok: true, msg: `${r.place_name} · ${r.rating?.toFixed(1)} ★ · ${r.reviews_found} recenzií` });
            toast.success("Spojenie funguje!");
        } catch (e) {
            setTestResult({ ok: false, msg: e.message });
            toast.error(e.message);
        } finally {
            setTesting(false);
        }
    };

    const loadReviews = async () => {
        setLoadingReviews(true);
        try {
            const data = await api.getGoogleReviews();
            setReviews(data);
        } catch {
            toast.error("Nepodarilo sa načítať recenzie");
        } finally {
            setLoadingReviews(false);
        }
    };

    const hasCredentials = !!settings.place_id && (!!settings.api_key_masked || !!apiKey);

    return (
        <div className="max-w-2xl space-y-8">
            <div>
                <h1 className="font-heading text-2xl font-black text-white tracking-tight">Google recenzie</h1>
                <p className="text-zinc-500 text-sm mt-1">
                    Prepojte Google Business profil na zobrazenie recenzií na webe.
                </p>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 bg-nova-surface/40 border border-white/8 rounded-xl">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-amber-400/10 border border-amber-400/25">
                        <Star className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                        <div className="text-white text-sm font-medium">
                            {settings.place_name || "Google Business"}
                        </div>
                        <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">
                            {settings.place_id ? `Place ID: ${settings.place_id}` : "Nie je nakonfigurované"}
                        </div>
                    </div>
                </div>
                <StatusBadge status={hasCredentials ? (testResult?.ok === false ? "error" : "ok") : "none"} />
            </div>

            {/* Settings form */}
            <div className="space-y-5 p-6 bg-nova-surface/30 border border-white/8 rounded-2xl">
                <h2 className="font-heading font-bold text-white text-base">Nastavenia pripojenia</h2>

                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Google Place ID *</label>
                    <input
                        value={settings.place_id}
                        onChange={e => setSettings(s => ({ ...s, place_id: e.target.value }))}
                        placeholder="napr. ChIJN1t_tDeuEmsRUsoyG83frY4"
                        className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40 font-mono"
                    />
                    <p className="mt-1.5 text-zinc-600 text-[11px]">
                        Nájdite Place ID na <span className="text-zinc-400">developers.google.com/maps/documentation/places/web-service/place-id</span>
                    </p>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            Google API Key {settings.api_key_masked && <span className="text-nova-cyan/70">· aktuálny: {settings.api_key_masked}</span>}
                        </label>
                        <button onClick={() => setShowKey(v => !v)} className="text-zinc-600 hover:text-zinc-400 transition-colors">
                            {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                    <input
                        type={showKey ? "text" : "password"}
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        placeholder={settings.api_key_masked ? "Nechajte prázdne pre zachovanie aktuálneho kľúča" : "Vložte Google API Key…"}
                        className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40 font-mono"
                    />
                    <p className="mt-1.5 text-zinc-600 text-[11px]">
                        Potrebujete API kľúč s povoleným Places API (Web Service).
                    </p>
                </div>

                <div className="flex gap-3 pt-1">
                    <button
                        onClick={save} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 bg-nova-cyan rounded-lg text-nova-black text-sm font-semibold hover:bg-nova-cyan/90 transition-colors disabled:opacity-50"
                    >
                        <Save className="w-3.5 h-3.5" />
                        {saving ? "Ukladám…" : "Uložiť nastavenia"}
                    </button>
                    <button
                        onClick={test} disabled={testing || !hasCredentials}
                        className="flex items-center gap-2 px-5 py-2.5 border border-white/10 rounded-lg text-zinc-300 text-sm hover:border-white/25 hover:text-white transition-colors disabled:opacity-40"
                    >
                        {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Otestovať spojenie
                    </button>
                </div>

                {testResult && (
                    <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2.5 border ${
                        testResult.ok
                            ? "bg-emerald-500/8 border-emerald-500/20 text-emerald-400"
                            : "bg-red-500/8 border-red-500/20 text-red-400"
                    }`}>
                        {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                        {testResult.msg}
                    </div>
                )}
            </div>

            {/* Review preview */}
            {hasCredentials && (
                <div className="space-y-4 p-6 bg-nova-surface/30 border border-white/8 rounded-2xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="font-heading font-bold text-white text-base">Náhľad recenzií</h2>
                            {reviews?.rating > 0 && (
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="font-heading text-xl font-black text-white">{reviews.rating.toFixed(1)}</span>
                                    <StarRow rating={Math.round(reviews.rating)} />
                                    <span className="font-mono text-[10px] text-zinc-500">{reviews.total} recenzií</span>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={loadReviews} disabled={loadingReviews}
                            className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-xs font-mono"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingReviews ? "animate-spin" : ""}`} />
                            Načítať
                        </button>
                    </div>
                    {reviews === null && !loadingReviews && (
                        <p className="text-zinc-600 text-sm text-center py-4">Kliknite "Načítať" pre zobrazenie recenzií.</p>
                    )}
                    {loadingReviews && (
                        <div className="space-y-2">
                            {[0,1,2].map(i => (
                                <div key={i} className="h-16 rounded-xl bg-nova-surface/40 animate-pulse" />
                            ))}
                        </div>
                    )}
                    {reviews?.reviews?.length > 0 && (
                        <div className="space-y-2">
                            {reviews.reviews.map((r, i) => <ReviewPreview key={i} review={r} />)}
                        </div>
                    )}
                    {reviews?.maps_url && (
                        <a href={reviews.maps_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-400/70 hover:text-amber-400 transition-colors">
                            <ExternalLink className="w-3 h-3" />
                            Otvoriť na Google Maps
                        </a>
                    )}
                </div>
            )}

            {/* How to get API key guide */}
            <div className="border border-white/6 rounded-2xl overflow-hidden">
                <button
                    onClick={() => setShowGuide(v => !v)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/3 transition-colors"
                >
                    <span className="text-white text-sm font-medium">Ako získať Google API Key a Place ID?</span>
                    {showGuide ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </button>
                {showGuide && (
                    <div className="px-6 pb-6 text-zinc-400 text-sm space-y-4 border-t border-white/6">
                        <ol className="space-y-3 mt-4 list-none">
                            {[
                                ["Google Cloud Console", "Choďte na console.cloud.google.com a vytvorte nový projekt (alebo použite existujúci)."],
                                ["Aktivujte Places API", "V sekcii APIs & Services → Library vyhľadajte \"Places API\" a kliknite Enable."],
                                ["Vytvorte API kľúč", "Choďte na APIs & Services → Credentials → Create Credentials → API Key. Skopírujte vygenerovaný kľúč."],
                                ["Obmedzte kľúč (odporúčané)", "Kliknite na kľúč → Application restrictions → IP addresses (pre server) alebo HTTP referrers."],
                                ["Nájdite Place ID", "Choďte na developers.google.com/maps/documentation/javascript/examples/places-placeid-finder — zadajte názov vašej firmy a skopírujte Place ID."],
                                ["Alternatívne Place ID", "V Google Maps vyhľadajte svoju firmu, kliknite na ňu pravým tlačidlom → \"Čo je tu?\" — URL obsahuje parameter CID, no Place ID je spoľahlivejší identifikátor."],
                            ].map(([title, desc], i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-mono text-[10px] text-zinc-600 w-5 shrink-0 mt-0.5">{String(i+1).padStart(2,"0")}</span>
                                    <div>
                                        <div className="font-medium text-white text-sm mb-0.5">{title}</div>
                                        <div className="text-zinc-500 text-xs leading-relaxed">{desc}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <a
                            href="https://console.cloud.google.com/apis/credentials"
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-nova-cyan hover:text-nova-cyan/80 transition-colors"
                        >
                            <ExternalLink className="w-3 h-3" />
                            Otvoriť Google Cloud Console
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
